import React, { useState, useEffect } from 'react';
import { aboutAPI } from '../services/api';
import { 
  Users, Target, Award, CheckCircle, Globe, Zap, Code, Smartphone, Palette, Database, Cloud, Shield, Settings, Mail, Phone, MapPin,
  ShoppingBag, Star, Download, Headphones, RefreshCw, BookOpen, AlertCircle, Play, FileText, ArrowRight, ArrowLeft, DollarSign, Send, ShoppingCart,
  Heart, Briefcase, Linkedin, Twitter, Github, ExternalLink
} from 'lucide-react';
import { gsap } from 'gsap';
import { useNotificationHook } from '../hooks/useNotificationHook';

const About = () => {
  const { notify } = useNotificationHook();
  const [aboutData, setAboutData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      const [contentResponse, teamResponse, documentsResponse] = await Promise.all([
        aboutAPI.getContent(),
        aboutAPI.getTeam(),
        aboutAPI.getDocuments()
      ]);

      if (contentResponse?.data?.success) {
        setAboutData(contentResponse.data.data);
      }

      if (teamResponse?.data?.success) {
        setTeamMembers(teamResponse.data.data);
      }

      if (documentsResponse?.data?.success) {
        setDocuments(documentsResponse.data.data);
      }
          } catch (err) {
        console.error('Error fetching about data:', err);
        notify.error('Failed to load about us content', 'Loading Error');
      } finally {
      setLoading(false);
    }
  };

  const getIconComponent = (iconName) => {
    const iconMap = {
      Target, Users, Award, CheckCircle, Globe, Zap, Code, Smartphone,
      Palette, Database, Cloud, Shield, Star, Heart, Download, FileText, Briefcase
    };
    return iconMap[iconName] || Target;
  };

  const getVideoEmbedUrl = (url, type) => {
    if (!url) return null;
    
    switch (type) {
      case 'youtube':
        const youtubeId = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&\n?#]+)/);
        return youtubeId ? `https://www.youtube.com/embed/${youtubeId[1]}` : url;
      case 'vimeo':
        const vimeoId = url.match(/vimeo\.com\/(\d+)/);
        return vimeoId ? `https://player.vimeo.com/video/${vimeoId[1]}` : url;
      case 'drive':
        // Convert Google Drive sharing URL to embed URL
        const driveId = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
        return driveId ? `https://drive.google.com/file/d/${driveId[1]}/preview` : url;
      case 's3':
      case 'direct':
        return url;
      default:
        return url;
    }
  };

  const inferVideoType = (url) => {
    if (!url) return 'direct';
    const lower = url.toLowerCase();
    if (lower.includes('youtube.com') || lower.includes('youtu.be')) return 'youtube';
    if (lower.includes('vimeo.com')) return 'vimeo';
    if (lower.includes('drive.google.com')) return 'drive';
    if (lower.includes('s3.amazonaws.com')) return 's3';
    return 'direct';
  };

  const getFileIcon = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return FileText;
      case 'doc':
      case 'docx':
        return FileText;
      case 'xls':
      case 'xlsx':
        return FileText;
      case 'ppt':
      case 'pptx':
        return FileText;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return FileText;
      default:
        return Download;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading about us...</p>
        </div>
      </div>
    );
  }

  if (error || !aboutData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">{error || 'Failed to load about us content'}</p>
        </div>
      </div>
    );
  }

  const videoType = inferVideoType(aboutData.video_url);
  const embedVideoUrl = getVideoEmbedUrl(aboutData.video_url, aboutData.video_type || videoType);

  return (
    <div className="min-h-screen">
      {/* Hero Section - Matching Homepage Design */}
      <section className="relative py-24 bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            {aboutData.main_title_simple || 'About'} <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">{aboutData.gradient_title || 'Niswa Info Tech'}</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              {aboutData.main_description || 'Transforming businesses through innovative digital solutions since 2019'}
            </p>
          </div>
        </div>
      </section>

      {/* Modern Story, Statistics, and What We Do Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left Column: Story + Statistics */}
            <div className="space-y-12">
              {/* Our Story */}
              {aboutData.story_title && aboutData.story_description && (
                <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-8 shadow-xl border border-white/20">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                    {aboutData.story_title}
                  </h2>
                  <div className="prose prose-lg text-gray-600 dark:text-gray-300">
                    {aboutData.story_description.split('\n').map((paragraph, index) => (
                      <p key={index} className="mb-4">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Statistics Cards */}
              <div className="grid grid-cols-2 gap-6">
                {aboutData.stats_items && aboutData.stats_items.length > 0 ? (
                  // Use stats_items from admin panel
                  aboutData.stats_items.map((item, index) => {
                    const IconComponent = getIconComponent(item.icon);
                    return (
                      <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-6 text-center shadow-xl border border-white/20 hover:scale-105 transition-all duration-500">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                          {item.value}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{item.title}</p>
                      </div>
                    );
                  })
                ) : (
                  // Fallback to legacy stats
                  <>
                    {/* Projects Completed */}
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-6 text-center shadow-xl border border-white/20 hover:scale-105 transition-all duration-500">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        {aboutData.stats_projects_completed || 500}+
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">Projects Completed</p>
                    </div>

                    {/* Happy Clients */}
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-6 text-center shadow-xl border border-white/20 hover:scale-105 transition-all duration-500">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <Heart className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        {aboutData.stats_happy_clients || 200}+
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">Happy Clients</p>
                    </div>

                    {/* Team Members */}
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-6 text-center shadow-xl border border-white/20 hover:scale-105 transition-all duration-500">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        {aboutData.stats_team_members || 50}+
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">Team Members</p>
                    </div>

                    {/* Years Experience */}
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-6 text-center shadow-xl border border-white/20 hover:scale-105 transition-all duration-500">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        {aboutData.stats_years_experience || 5}+
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">Years Experience</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right Column: What We Do Card */}
            <div className="relative flex items-center">
              <div className="absolute left-0 right-0 top-1/2 h-2/3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-xl opacity-30 transform rotate-6 scale-105"></div>
              <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-8 w-full shadow-xl border border-white/20 relative">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {aboutData.what_we_do_title || 'What We Do'}
                </h2>
                <div className="space-y-4">
                  {aboutData.what_we_do_items && aboutData.what_we_do_items.length > 0 ? (
                    aboutData.what_we_do_items.map((item, idx) => {
                      const IconComponent = getIconComponent(item.icon);
                      return (
                        <div key={idx} className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                            <IconComponent className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{item.title}</h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">{item.description}</p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-400">No services added yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Core Values Section */}
      {aboutData.features_title && aboutData.features_items?.length > 0 && (
        <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-purple-800 to-slate-800 opacity-20"></div>
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
          </div>
          
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8">
                {aboutData.features_title}
              </h2>
              {aboutData.features_description && (
                <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                  {aboutData.features_description}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {aboutData.features_items.map((item, index) => {
                const IconComponent = getIconComponent(item.icon);
                return (
                  <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 hover:scale-105 transition-all duration-500 border border-white/20">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-center">
                      {item.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Modern Team Section */}
      {aboutData.team_title && teamMembers.length > 0 && (
        <section className="py-24 bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
          </div>
          
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8">
                {aboutData.team_title}
              </h2>
              {aboutData.team_description && (
                <p className="text-xl text_GRAY-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
                  {aboutData.team_description}
                </p>
              )}
            </div>
            <div className={`${teamMembers.length === 1 
              ? 'grid grid-cols-1 gap-8 place-items-center' 
              : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'}`}>
              {teamMembers.map((member) => (
                <div key={member.id} className="card-3d bg_WHITE/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 text-center hover:scale-105 transition-all duration-500 border border-white/20">
                  <div className="w-80 h-80 sm:w-48 sm:h-48 md:w-56 md:h-56 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 overflow-hidden shadow-lg">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {member.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                    {member.bio}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center mb-4">
                    {member.skills?.map((skill, index) => (
                      <span key={index} className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                  <div className="flex justify-center gap-3">
                    {member.email && (
                      <a href={`mailto:${member.email}`} className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors">
                        <Mail className="w-5 h-5" />
                      </a>
                    )}
                    {member.linkedin_url && (
                      <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors">
                        <Linkedin className="w-5 h-5" />
                      </a>
                    )}
                    {member.twitter_url && (
                      <a href={member.twitter_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors">
                        <Twitter className="w-5 h-5" />
                      </a>
                    )}
                    {member.github_url && (
                      <a href={member.github_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors">
                        <Github className="w-5 h-5" />
                      </a>
                    )}
                    {member.website_url && (
                      <a href={member.website_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors">
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Modern Video Section */}
      {aboutData.video_title && aboutData.video_url && (
        <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via_WHITE/5 to-transparent"></div>
          </div>
          
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8">
                {aboutData.video_title}
              </h2>
              {aboutData.video_description && (
                <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
                  {aboutData.video_description}
                </p>
              )}
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="card-3d bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-3xl p-8 shadow-3d border border-white/20 dark:border-slate-700/50">
                <div className="relative aspect-video rounded-2xl overflow-hidden">
                  <iframe
                    src={embedVideoUrl}
                    title={aboutData.video_title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Modern Company Documents Section */}
      {aboutData.documents_title && documents.length > 0 && (
        <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-purple-800 to-slate-800 opacity-20"></div>
            <div className="absolute top-0 left-0 w_full h_full bg-gradient-to-br from-transparent via_WHITE/5 to-transparent"></div>
          </div>
          
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text_WHITE mb-8">
                {aboutData.documents_title}
              </h2>
              {aboutData.documents_description && (
                <p className="text-xl text_WHITE/90 max-w-3xl mx-auto leading-relaxed">
                  {aboutData.documents_description}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {documents.map((document) => {
                const IconComponent = getIconComponent(document.download_button_icon);
                return (
                  <div key={document.id} className="card-3d bg_white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-8 shadow-xl hover:scale-105 transition-all duration-500 border border-white/20">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                          {document.title}
                        </h3>
                        {document.description && (
                          <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                            {document.description}
                          </p>
                        )}
                        {document.file_size && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {document.file_size}
                          </p>
                        )}
                      </div>
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center ml-4 shadow-lg">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <a
                      href={document.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl hover:scale-105 transition-all duration-300 shadow-lg"
                    >
                      <Download className="w-4 h-4" />
                      {document.download_button_text || 'Download'}
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Modern CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 dark:from-blue-600 dark:via-purple-600 dark:to-indigo-600 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-100/20 via-purple-100/20 to-indigo-100/20 dark:from-blue-700/20 dark:via-purple-700/20 dark:to-indigo-700/20"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            {aboutData?.cta_title || 'Ready to Transform Your Business?'}
          </h2>
          <p className="text-xl text-gray-600 dark:text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
            {aboutData?.cta_description || 'Let\'s discuss your project and create something amazing together. Get a free consultation and quote today.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a 
              href={aboutData?.cta_button1_link || '/contact'} 
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-500 flex items-center justify-center gap-3 shadow-glow"
            >
              {aboutData?.cta_button1_text || 'Start Your Project'}
              <ArrowRight className="w-6 h-6" />
            </a>
            <a 
              href={aboutData?.cta_button_link || '/quote'} 
              className="card-3d border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-gray-900 hover:text-white dark:hover:bg_WHITE dark:hover:text-gray-900 hover:scale-105 transition-all duration-500 shadow-glow"
            >
              {aboutData?.cta_button_text || 'Get Free Quote'}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
